import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import "./index.scss";
import * as _ from "lodash";
import { BadgeTypes } from "../../../../../../constant/enums";
import {
  addProfileAppearance,
  editProfileAppearance,
} from "../../../../../../store/profile-appearance/thunkAction";
import { useTranslation } from "react-i18next";
import { getImageUrl } from "../../../../../../utils/image";
import {
  sendNotifyIziMessageError,
  sendNotifyIziMessageSuccess,
} from "../../../../../../utils/event";

const AppearanceChatBadge = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const params = useParams();
  const profileId = _.get(params, "id");
  const navigate = useNavigate();
  const profileAppearanceManage = useSelector(
    (state) => state.profileAppearanceManage
  );
  const profileAppearance = _.get(profileAppearanceManage, "profileAppearance");
  const profileAppearanceId = _.get(profileAppearance, "_id");
  const isLoadingProfileAppearance = _.get(
    profileAppearance,
    "isLoadingProfileAppearance",
    false
  );
  const [enabled, setEnabled] = useState(false);
  const [badgeType, setBadgeType] = useState(BadgeTypes.IMAGE_LEFT);
  const [backgroundColor, setBackgroundColor] = useState("");
  const [textColor, setTextColor] = useState("");
  const [text, setText] = useState("");
  const [image, setImage] = useState("/img/chat/empty.jpg");
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);

  useEffect(() => {
    const chatBadge = _.get(profileAppearance, "chatBadge", {});

    setEnabled(_.get(chatBadge, "enabled", false));
    setBadgeType(_.get(chatBadge, "badgeType", BadgeTypes.IMAGE_LEFT));
    setBackgroundColor(_.get(chatBadge, "backgroundColor", ""));
    setTextColor(_.get(chatBadge, "textColor", ""));
    setText(_.get(chatBadge, "text", ""));
    setImage(_.get(profileAppearance, "imageUrl", "/img/chat/empty.jpg"));
    setTitle(_.get(chatBadge, "title", ""));
  }, [profileAppearance]);

  const saveChatBadge = async () => {
    const chatBadge = {
      enabled,
      badgeType,
      backgroundColor,
      textColor,
      text,
      title,
    };

    const formData = new FormData();

    formData.append("chatBadge", JSON.stringify(chatBadge));

    if (file) {
      formData.append("file", file);
    }

    try {
      if (profileAppearanceId) {
        await dispatch(
          editProfileAppearance({
            id: profileAppearanceId,
            data: formData,
            isFormData: !!file,
          })
        ).unwrap();

        sendNotifyIziMessageSuccess(
          t("profile.profile_appearance_save_success")
        );

        return;
      }

      formData.append("profileId", profileId);
      formData.append("window", JSON.stringify({}));
      formData.append("content", JSON.stringify({}));

      if (isLoadingProfileAppearance) {
        return;
      }

      await dispatch(
        addProfileAppearance({ data: formData, isFormData: !!file })
      ).unwrap();
      sendNotifyIziMessageSuccess(t("profile.profile_appearance_save_success"));
    } catch (error) {
      const errorCode = _.get(error, "errorCode");
      if (errorCode === "FILE_NOT_ALLOWED") {
        sendNotifyIziMessageError(t("profile.upload_file_error"));
      } else if (errorCode === "FILE_TOO_LARGE") {
        sendNotifyIziMessageError(t("profile.file_too_large"));
      } else {
        sendNotifyIziMessageError(t("profile.profile_appearance_save_error"));
      }
    }
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div
      className="tab-pane fade active show"
      id="badge-tab-pane"
      role="tabpanel"
      aria-labelledby="badge-tab"
      tabIndex="0"
    >
      <div className="row mb-5">
        <div className="col-lg-3">
          <h5 className="title">
            {t("settings.profile.profile_appearance.configurations")}
          </h5>
        </div>
        <div className="col-lg-7">
          <div className="row g-4">
            <div className="col-lg-3">
              <label className="form-label text-uppercase">
                {t("settings.profile.chat_badge")}
              </label>
              <label className="form-check form-switch gap-2 d-flex align-items-center">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  checked={enabled}
                  onChange={(e) => {
                    setEnabled(e.target.checked);
                  }}
                />
                <span>
                  {enabled ? t("common.enabled") : t("common.disabled")}
                </span>
              </label>
            </div>
            <div className="col-lg-9">
              <label className="form-label text-uppercase">
                {t("settings.profile.profile_appearance.header_title")}
              </label>
              <input
                className="form-control"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>

            <div className="col-12">
              <label className="form-label text-uppercase">
                {t("settings.profile.profile_appearance.badge_type")}
              </label>
              <div className="d-flex flex-wrap gap-3 btn-img-box">
                <button
                  className={
                    badgeType === BadgeTypes.IMAGE_LEFT
                      ? "btn btn-img active"
                      : "btn btn-img"
                  }
                  onClick={() => setBadgeType(BadgeTypes.IMAGE_LEFT)}
                >
                  <img width="68px" src="/img/chat/btn3.png" alt="" />
                  <div className="mt-4">
                    {t("settings.profile.profile_appearance.image_left")}
                  </div>
                  <i className="fa fa-check-circle"></i>
                </button>
                <button
                  onClick={() => setBadgeType(BadgeTypes.IMAGE_RIGHT)}
                  className={
                    badgeType === BadgeTypes.IMAGE_RIGHT
                      ? "btn btn-img active"
                      : "btn btn-img"
                  }
                >
                  <img width="68px" src="/img/chat/btn4.png" alt="" />
                  <div className="mt-4">
                    {t("settings.profile.profile_appearance.image_right")}
                  </div>
                  <i className="fa fa-check-circle"></i>
                </button>
                <button
                  onClick={() => setBadgeType(BadgeTypes.TEXT_ONLY)}
                  className={
                    badgeType === BadgeTypes.TEXT_ONLY
                      ? "btn btn-img active"
                      : "btn btn-img"
                  }
                >
                  <img width="68px" src="/img/chat/btn5.png" alt="" />
                  <div className="mt-4">
                    {t("settings.profile.profile_appearance.text_only")}
                  </div>
                  <i className="fa fa-check-circle"></i>
                </button>
                <button
                  onClick={() => setBadgeType(BadgeTypes.CUSTOM_IMAGE)}
                  className={
                    badgeType === BadgeTypes.CUSTOM_IMAGE
                      ? "btn btn-img active"
                      : "btn btn-img"
                  }
                >
                  <img width="68px" src="/img/chat/btn6.png" alt="" />
                  <div className="mt-4">
                    {t("settings.profile.profile_appearance.custom_image")}
                  </div>
                  <i className="fa fa-check-circle"></i>
                </button>
              </div>
            </div>
            <div className="col-lg-6">
              <label className="form-label text-uppercase">
                {t("settings.profile.profile_appearance.background_color")}
              </label>
              <div className="d-flex gap-2  align-items-center">
                <input
                  type="color"
                  className="form-control form-control-color"
                  id="exampleColorInput"
                  value={backgroundColor}
                  onChange={(e) => setBackgroundColor(e.target.value)}
                />
                <div
                  className="btn-copy btn btn-gray btn-outline-gray bg-white"
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  data-bs-original-title=""
                  title=""
                >
                  {backgroundColor}{" "}
                  <span className="color-info ms-5">{t("common.copy")}</span>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <label className="form-label text-uppercase">
                {t("settings.profile.profile_appearance.text_color")}
              </label>
              <div className="d-flex gap-2  align-items-center">
                <input
                  type="color"
                  className="form-control form-control-color"
                  id="exampleColorInput"
                  value={textColor}
                  title="Choose your color"
                  onChange={(e) => setTextColor(e.target.value)}
                />
                <div
                  className="btn-copy btn btn-gray btn-outline-gray bg-white"
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  data-bs-title="Click to copy"
                  data-bs-original-title=""
                  title=""
                >
                  {textColor}{" "}
                  <span className="color-info ms-5">{t("common.copy")}</span>
                </div>
              </div>
            </div>
            <div className="col-12">
              <label className="form-label text-uppercase">
                {t("settings.profile.profile_appearance.text")}
              </label>
              <input
                type="text"
                className="form-control"
                value={text}
                onChange={(e) => setText(e.target.value)}
              />
            </div>
            <div className="col-12">
              <div className="d-flex gap-4">
                <img
                  className="avt-image"
                  id="imgAvt"
                  src={getImageUrl(image)}
                  alt=""
                />
                <div>
                  <div className="position-relative">
                    <input
                      type="file"
                      id="imgInp"
                      accept="image/*"
                      className="form-control opacity-0"
                      onChange={handleImageChange}
                    />
                    <span className="btn btn-info-light w-100 pe-none position-absolute top-0 start-0">
                      <i className="fa fa-upload"></i>{" "}
                      {t("settings.profile.profile_appearance.upload_logo")}
                    </span>
                  </div>
                  <div className="mt-4">
                    - {t("settings.profile.profile_appearance.logo_desc")}
                    <br />-{" "}
                    {t("settings.profile.profile_appearance.file_logo_allowed")}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="card-footer">
        <div className="d-flex gap-3 justify-content-center align-items-center">
          <button onClick={saveChatBadge} className="btn btn-primary">
            {t("common.save")}
          </button>
          <button
            onClick={() => {
              navigate(`/profile/${profileId}/detail`);
            }}
            className="btn btn-gray btn-outline-gray"
          >
            {t("common.exit")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AppearanceChatBadge;
